import React, { useState, useEffect } from 'react';
import { ArrowLeft, CircleCheckBig, ExternalLink, Copy, Shield, Calendar } from 'lucide-react';
import { FaWhatsapp, FaRegEnvelope } from 'react-icons/fa';
import { FiTwitter, FiLinkedin } from 'react-icons/fi';

interface PassportDetailsProps {
  credential: any;
  onBack: () => void;
  onTabChange: (tab: string) => void;
  profileName: string;
  username?: string; 
}

const PassportDetails: React.FC<PassportDetailsProps> = ({ credential, onBack, onTabChange, profileName, username }) => {

  // let fullName = profileData?.fullName || profileData?.first_name || '';
  // fullName = profileData?.last_name ? fullName + profileData?.last_name : fullName;
  // const credentialId = credential.uuid || credential.unique_code || credential.id; 

  const credentialId = credential.unique_code || credential.id;
  const title = credential.title || credential.name ||  credential.skillstree?.name || credentialId || 'Credential name not found';
  const microskill = credential.microskill || 'Undefined microskill';
  const skillstree = typeof credential.skillstree === 'string' ? credential.skillstree : (typeof credential.skillstree === 'object' ? (credential.skillstree?.name || credential.skillstree?.code || 'Undefined skillstree') :  'Undefined skillstree');
  const issuedOn = credential.earned_at || credential.completed_at || null;
  const expiresOn = credential.expires_at || null;
  const microskills = credential.microskills || credential.badges || [];
  const earnedBadges = credential.earnedBadges || microskills?.length || null;
  const totalMicroskills = credential.totalMicroskills || earnedBadges || null;
  const completedMicroskills = credential.completedMicroskills || earnedBadges || null;
  const image = credential.image_url || credential.image || (typeof credential.skillstree === 'object' ? credential.skillstree?.image_url : null) || '/pages/achievements/skilltrue-passport.png';
  const defaultImage = '/pages/achievements/skilltrue-passport.png';

  const [badgeImages, setBadgeImages] = useState<string[]>([]);
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);
  const baseUrl = import.meta.env.VITE_APP_URL || 'https://dev.skillsture.io';
  const shareUrl = `${baseUrl}/profile/${username}/passports/${credential.id}`;

  // Sharing functions
  const handleShareLinkedIn = () => {
    const linkedInUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
    window.open(linkedInUrl, '_blank', 'width=600,height=400');
  };

  const handleShareTwitter = () => {
    const text = `I earned the "${credential.title || credential.name}" Passport from SKILLSTURE! Check it out:`;
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, '_blank', 'width=600,height=400');
  };

  const handleShareWhatsApp = () => {
    const text = `Check out my ${credential.title || credential.name} Passport from SKILLSTURE!`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleShareEmail = () => {
    const subject = `My ${credential.title || credential.name} Passport from SKILLSTURE`;
    const body = `I wanted to share my "${credential.title || credential.name}" Passport with you.\n\nView it here: ${shareUrl}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const handleCopyId = async () => {
    try {
      await navigator.clipboard.writeText(credential.id);
      alert('Credential ID copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  // Programmatically get badge images
  useEffect(() => {
    const importBadgeImages = async () => {
      try {
        // This will work if you're using a bundler like Webpack or Vite
        const context = (require as any).context('../../public/badge', true, /\.png$/);
        const imagePaths = context.keys().map((key: string) => key.replace('./', '/badge/'));
        setBadgeImages(imagePaths);
      } catch (error) {
        console.warn('Could not dynamically import badge images, using fallback');
        // Fallback to a few common images if dynamic import fails
        setBadgeImages([
          "/badge/big-data/data-scientist.png",
          "/badge/cloud-computing/cloud-architect-cloud-engineer.png",
          "/badge/cybersecurity/senior-risk-analyst.png",
          "/badge/digital-product-design/ux-designer-application-web-.png",
          "/badge/product-marketing/digital-marketing-strategist.png"
        ]);
      }
    };

    importBadgeImages();
  }, []);

  const getRandomBadgeImage = () => {
    if (badgeImages.length === 0) return "/badge/product-marketing/hs58-dm.png";
    return badgeImages[Math.floor(Math.random() * badgeImages.length)];
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx || !img) return;

    canvas.width = img?.naturalWidth || 0;
    canvas.height = img?.naturalHeight || 0;
    ctx.drawImage(img, 0, 0);

    try {
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      // Find non-transparent bounds
      let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;

      for (let y = 0; y < canvas.height; y++) {
        for (let x = 0; x < canvas.width; x++) {
          const alpha = data[(y * canvas.width + x) * 4 + 3];
          if (alpha > 0) {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
          }
        }
      }

      const contentWidth = maxX - minX;
      const contentHeight = maxY - minY;

      if (contentWidth > 0 && contentHeight > 0) {
        setImageDimensions({
          width: contentWidth,
          height: contentHeight
        });
      } else {
        // Fallback to original dimensions if transparency detection fails
        setImageDimensions({
          width: img.naturalWidth,
          height: img.naturalHeight
        });
      }
    } catch (error) {
      // Fallback to original dimensions
      setImageDimensions({
        width: img.naturalWidth,
        height: img.naturalHeight
      });
    }
  };

  const getImageSizeClass = () => {
    if (!imageDimensions) return 'max-h-[50%] max-w-[50%]';

    const { width, height } = imageDimensions;
    const aspectRatio = width / height;

    // If image is wider than tall, constrain by height
    if (aspectRatio > 1.2) {
      return 'max-h-[50%] w-auto';
    }
    // If image is taller than wide, constrain by width
    else if (aspectRatio < 0.8) {
      return 'h-auto max-w-[50%]';
    }
    // For roughly square images, use both constraints
    else {
      return 'max-h-[50%] max-w-[50%]';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const randomBadgeImage = getRandomBadgeImage();

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-7xl mx-auto">
        {/* Back Button */}
        <div className="mb-6 border-b border-white ">
          <button
            onClick={onBack}
            className="flex items-center bg-transparent text-white hover:text-white hover:bg-gray-500/20 outline-none border-0 transition-colors"
          >
            <ArrowLeft size={16} className="mr-2 mt-0.5"/> Back to Profile
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header Card */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="bg-gradient-to-br from-blue-800 via-orange-400 to-blue-900 h-72 relative">

                {/* Passport Preview */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <img
                    className="w-auto h-48"
                    src={image}
                    alt={title}
                    onLoad={handleImageLoad}
                    onError={(e) => {
                      e.currentTarget.src = defaultImage;
                      e.currentTarget.onload = () => handleImageLoad(e);
                    }}
                  />
                </div>
              </div>
              
              <div className="p-6 bg-slate-50 border-t border-slate-200 flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                  <CircleCheckBig size={24} className=""/> 
                </div>
                <div>
                  <p className="text-sm text-slate-500">Issued by</p>
                  <p className="text-base font-semibold text-slate-900">{credential.issuer?.name || credential.issuer || 'SKILLSTURE'}</p>
                  <a href={credential.issuer?.website || credential.issuerLink || '#'} className="text-sm font-normal text-blue-600 hover:text-blue-800">
                    Visit Website <ExternalLink size={14} className="inline ms-1 -mt-1"/>
                  </a>
                </div>
              </div>
            </div>

            {/* Details Card */}
            <div className="bg-white rounded-md shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-slate-900 mb-2">{title}</h2>
              <p className="text-slate-600 mb-6 text-base font-normal">Awarded for completing {title}</p>

              {/* Recipient Info */}
              <div className="mb-6">
                <h3 className="text-sm font-normal text-slate-500 mb-3">Credential Recipient</h3>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center">
                    <span className="text-white font-normal">{profileName.charAt(0)}</span>
                  </div>
                  <div>
                    <p className="text-slate-900 font-normal">{profileName}</p>
                    <a onClick={() => onTabChange('all')} className="text-blue-600 hover:text-blue-800 text-sm font-normal">
                      View All Credentials
                    </a>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-2 md:gap-4">
                <div className="space-y-4 border-t border-slate-200 pt-6">
                  {/* Skillstree */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-1">Skillstree</h4>
                    <span className="inline-block bg-purple-100 text-purple-800 text-xs font-semibold px-3 py-1 rounded-full">
                      {skillstree || title}
                    </span>
                  </div>

                  {/* Job Role */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-1">Job Role Credential</h4>
                    <p className="text-slate-900">{skillstree}</p>
                  </div>

                  {/* Passport ID */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-1">Passport ID</h4>
                    <p className="text-slate-900 font-mono">{credentialId}</p>
                  </div>

                  {/* Completion Status */}
                  <div>
                    <h4 className="text-sm font-medium text-slate-500 mb-1">Completion Status</h4>
                    <p className="text-slate-900 font-medium mb-1">{completedMicroskills || 0}/{totalMicroskills || 1} Microskills Completed</p>
                    <p className="text-slate-600 text-sm">{earnedBadges || 0} Badges Earned</p>
                  </div>
                </div>

                {/* Completed Microskills */}
                <div className="pt-6">
                  <h4 className="text-sm font-medium text-slate-500 mb-3">Completed Microskills</h4>
                  <div className="space-y-2">
                    {microskills && microskills?.map((skill, index) => (
                      <div key={index} className="flex items-center gap-3">
                        <CircleCheckBig size={18} className="inline text-green-600"/>
                        <span className="text-slate-700 text-sm">{skill.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 border-t border-slate-200 pt-6">
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-1">Issued On</h4>
                  <div className="flex items-center gap-2">
                    <Calendar size={18} className="inline text-slate-400"/>
                    <span className="text-slate-900">{issuedOn ? new Date(issuedOn).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Unobtained'}</span>
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-1">Expires On</h4> 
                  <div className="flex items-center gap-2">                   
                    {credential.expiresOn && <Calendar size={18} className="inline text-slate-400"/>}
                    <span className="text-slate-900">{expiresOn ? new Date(expiresOn).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : 'Does not expire'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Share Card */}
            <div className="bg-white rounded-md shadow-lg p-6">
              <div className="flex items-center gap-3 mb-4">
                <img className="w-auto h-12 rounded-sm" src="/pages/achievements/skilltrue-passport.png" alt="Share" />
                <div>
                  <h3 className="font-normal text-slate-900">Share Credential</h3>
                  <p className="text-sm text-slate-600/80">Show this credential on your social network</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 mb-3">
                <button 
                  onClick={handleShareLinkedIn} 
                  className="flex items-center text-sm font-normal justify-center gap-2 bg-sky-600 text-white py-1 px-3 rounded-sm hover:bg-sky-700 transition-colors"
                >
                  <FiLinkedin size={14} className=""/> <span>LinkedIn</span>
                </button>
                <button 
                  onClick={handleShareTwitter} 
                  className="flex items-center text-sm font-normal justify-center gap-2 bg-sky-500 text-white py-1 px-3 rounded-sm hover:bg-sky-600 transition-colors"
                >
                  <FiTwitter size={14} className=""/> <span>Twitter</span>
                </button>
                <button 
                  onClick={handleShareWhatsApp} 
                  className="flex items-center text-sm font-normal justify-center gap-2 bg-green-500 text-white py-1 px-3 rounded-sm hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp size={14} className=""/> <span>WhatsApp</span>
                </button>
                <button 
                  onClick={handleShareEmail} 
                  className="flex items-center text-sm font-normal justify-center gap-2 bg-white border border-gray-300 text-gray-700 py-1 px-3 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  <FaRegEnvelope size={14} className=""/> <span>Email</span>
                </button>
              </div>

              <button onClick={handleCopyLink} className="w-full flex items-center justify-center bg-white border border-gray-300 text-gray-700 py-1 rounded-sm hover:bg-gray-50 transition-colors">
                <Copy size={14} className="inline me-3"/> <span className="text-sm font-semibold">Copy Link </span>
              </button>
            </div>

            {/* Verification Card */}
            <div className="bg-white rounded-md shadow-lg p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Credential Verification</h3>
              
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
                  <div className="flex items-start gap-3">
                    <CircleCheckBig size={18} className="inline mt-0.5 text-blue-600"/> 
                    <div>
                      <p className="text-blue-900 text-xs font-medium mb-1">This credential is from a verified issuer</p>
                      <p className="text-blue-700 text-xs font-light">Verified by blockchain technology</p>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
                  <div className="flex items-start gap-3">
                    <Shield size={18} className="inline mt-0.5 text-amber-600"/> 
                    <div>
                      <p className="text-amber-900 text-xs font-medium mb-1">Secured by Blockchain</p>
                      <p className="text-amber-700 text-xs font-light">Immutable and tamper-proof credential record</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Credential ID</h4>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={credential.id}
                      readOnly
                      className="flex-1 bg-slate-100 border border-slate-300 rounded py-1 px-3 text-sm text-black"
                    />
                    <button onClick={handleCopyId} className="bg-white border border-gray-300 rounded-sm py-1 px-2 hover:bg-gray-50 transition-colors">
                      <Copy size={14} className="inline text-black"/>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Issuer Info Card */}
            <div className="bg-white rounded-md shadow-lg p-6">
              <h3 className="font-normal text-slate-900 mb-4">More about the Issuer</h3>
              
              <div className="flex items-start gap-4 mb-4">
                <img className="w-16 h-16 rounded-sm" src="/favicon/android-chrome-512x512.png" alt="Issuer" />
                <div className='leading-none'>
                  <h4 className="font-normal text-slate-900 mb-1">SKILLSTURE</h4>
                  <p className="text-sm text-slate-600 mb-2 text-justify">
                    SKILLSTURE is Malaysia's leading digital microlearning platform, powered by PEOPLElogy Digital Sdn Bhd
                  </p>
                  <a href="https://peoplelogy.com" target='_blank' className="font-normal text-blue-600 hover:text-blue-800 text-sm">
                    Visit Issuer Website <ExternalLink size={14} className="inline ms-1 -mt-1"/>
                  </a>
                </div>
              </div>

              <div className="border-t border-slate-200 pt-4">
                <h4 className="text-sm font-medium text-slate-600 mb-3">More credentials from this Issuer</h4>
                <button onClick={() => onTabChange('all')} className="w-full text-sm bg-white border border-gray-300 text-gray-700 py-1 px-2 rounded-sm hover:bg-gray-50 transition-colors">
                  View All Credentials
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PassportDetails;